import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";
import { RootState } from "../redux/services/store";
import {
    useGetAllBranchesQuery,
    useCreateBranchMutation,
    useUpdateBranchMutation,
    useDeleteBranchMutation,
    useGetManagersQuery,
    BranchRaw,
} from "../redux/services/branchApi";
import { useState, useEffect } from "react";

export interface BranchData {
    id: string;
    branchName: string;
    address: string;
    location: string;
    branchEmail: string;
    managerName: string;
    managerId: string;
    contactNumber: string;
    status?: string;
    activeOrders: string;
    time: string;
}

export const useBranchesController = (
    branchId?: string,
    currentPage: number = 1,
    pageSize: number = 10
) => {
    const user = useSelector((state: RootState) => state.auth.user);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

    // Fetch paginated branches for table only
    const paginatedBranchesQuery = useGetAllBranchesQuery(
        user?.id
            ? { page: currentPage, limit: pageSize, user_id: user.id }
            : skipToken,
        { refetchOnMountOrArgChange: true }
    );

    // Fetch global totals (only page 1 needed)
    const globalBranchesQuery = useGetAllBranchesQuery(
        user?.id
            ? { page: 1, limit: 1000, user_id: user.id } 
            : skipToken
    );

    const [createBranch] = useCreateBranchMutation();
    const [updateBranch] = useUpdateBranchMutation();
    const [deleteBranch] = useDeleteBranchMutation();

    const { data: managersData, isLoading: isManagersLoading, error: managersError } =
        useGetManagersQuery();

    const managers =
        managersData?.data?.staff_members?.map((m) => ({
            id: String(m.id),
            name: m.user?.name ?? "Unknown",
        })) ?? [];

    const handleCreateBranch = async (data: Partial<BranchData>) => {
        const payload = {
            branch_name: data.branchName ?? "",
            branch_address: data.address ?? "",
            location: data.location ?? "",
            branch_email: data.branchEmail ?? "",
            branch_phone: data.contactNumber ?? "",
            manager_id: data.managerId ?? "",
        };
        await createBranch(payload).unwrap();
        await paginatedBranchesQuery.refetch();
        await globalBranchesQuery.refetch();
    };

    const handleUpdateBranch = async (id: string, data: Partial<BranchData>) => {
        const payload = {
            branch_name: data.branchName ?? "",
            branch_address: data.address ?? "",
            location: data.location ?? "",
            branch_email: data.branchEmail ?? "",
            branch_phone: data.contactNumber ?? "",
            manager_id: data.managerId ?? "",
        };
        await updateBranch({ id, data: payload }).unwrap();
        await paginatedBranchesQuery.refetch();
        await globalBranchesQuery.refetch();
    };

    const handleDeleteBranch = async (id: string) => {
        await deleteBranch(id).unwrap();
        await paginatedBranchesQuery.refetch();
        await globalBranchesQuery.refetch();
    };

    const paginatedRawData = paginatedBranchesQuery.data;
    const branchArray: BranchRaw[] = paginatedRawData?.data?.branchInfo?.data ?? [];

    const mappedBranches: BranchData[] = branchArray.map((b) => ({
        id: String(b.id),
        branchName: b.branch_detail?.branch_name ?? "N/A",
        address: b.branch_detail?.branch_address ?? "N/A",
        location: b.branch_detail?.location ?? "N/A",
        branchEmail: b.branch_detail?.branch_email ?? "N/A",
        managerName: b.branch_manager?.manager_name ?? "N/A",
        managerId: String(b.branch_manager?.manager_id ?? ""),
        contactNumber: b.branch_detail?.branch_phone ?? "N/A",
        status:
            b.branch_detail?.status === "active"
                ? "Active"
                : b.branch_detail?.status === "deactive"
                    ? "Inactive"
                    : "",
        activeOrders: b.active_orders ?? "0",
        time: b.time ?? "N/A",
    }));

    // Use global totals from separate query
    const globalRawData = globalBranchesQuery.data;
    const totalBranches = globalRawData?.data?.branchInfo?.meta?.total ?? 0;
    const activeCount = globalRawData?.data?.total_active_branch ?? 0;
    const inActiveCount = globalRawData?.data?.total_inactive_branch ?? 0;

    const totalPages = Math.ceil(totalBranches / pageSize);

    return {
        showBulkUploadModal,
        setShowBulkUploadModal,

        branches: mappedBranches,
        totalBranches,
        activeCount,
        inActiveCount,
        totalPages,

        refetchBranches: paginatedBranchesQuery.refetch,
        isBranchesLoading: paginatedBranchesQuery.isLoading,

        createBranch: handleCreateBranch,
        updateBranch: handleUpdateBranch,
        deleteBranch: handleDeleteBranch,

        managers,
        isManagersLoading,
        managersError,
    };
};
